import { useEffect, useState, useMemo } from 'react';
import {
  Button,
  Field,
  Input,
  Combobox,
  Option,
  Card,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Info16Regular, Add20Regular, Dismiss12Regular } from '@fluentui/react-icons';
import {
  AscoraJobSummary, Take5Submission, M365Profile, SupervisorCandidate, SiteContact, AppConfig,
} from '../types';
import { getJobSwms } from '../services/api';
import { PhaseStrip } from './PhaseStrip';
import { ReadOnlyField } from './ReadOnlyField';
import { TapRow } from './TapRow';
import { SourceTag } from './SourceTag';
import { formatDateAU, formatTimeAU } from '../services/risk';

interface Props {
  job: AscoraJobSummary;
  config: AppConfig;
  initial: Partial<Take5Submission['stop']>;
  profile: M365Profile | null;
  supervisorCandidates: SupervisorCandidate[];
  siteContact: SiteContact | null;
  onNext: (data: Take5Submission['stop']) => void;
  onShowInfo: (key: string) => void;
}

interface PPEItemState { id: string; label: string; on: boolean; custom: boolean; }

const useStyles = makeStyles({
  body: { flex: 1, overflowY: 'auto', paddingTop: '16px', paddingBottom: '20px', paddingLeft: '18px', paddingRight: '18px' },
  section: {
    marginBottom: '14px',
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '16px',
    paddingRight: '16px',
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
    background: tokens.colorNeutralBackground1,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  stepNum: {
    backgroundColor: '#E3272D',
    color: '#FFFFFF',
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    flexShrink: 0,
  },
  infoBtn: {
    marginLeft: 'auto',
    minWidth: '44px',
    minHeight: '44px',
  },
  fieldGrid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
  },
  ppeGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    marginTop: '4px',
  },
  ppeChipWrap: {
    position: 'relative',
  },
  ppeRemove: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '24px',
    height: '24px',
    background: 'transparent',
    borderTopWidth: '0',
    borderRightWidth: '0',
    borderBottomWidth: '0',
    borderLeftWidth: '0',
    cursor: 'pointer',
    color: tokens.colorNeutralForeground3,
    padding: 0,
    lineHeight: 1,
    zIndex: 2,
  },
  customAddRow: {
    marginTop: '10px',
    display: 'flex',
    gap: '8px',
  },
  banner: {
    backgroundColor: '#FFF3F4',
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: '#E3272D',
    color: '#6F0F12',
    borderRadius: '16px',
    paddingTop: '13px',
    paddingBottom: '13px',
    paddingLeft: '14px',
    paddingRight: '14px',
    fontSize: '13px',
    lineHeight: 1.5,
    marginBottom: '12px',
    fontWeight: 500,
  },
  /* Combobox needs to NOT zoom on iPhone — input is forced 16px via fontSize prop */
  comboInput: {
    fontSize: '16px !important',
  },
  cta: {
    width: '100%',
    marginTop: '8px',
    minHeight: '52px',
    fontSize: '15px',
    fontWeight: 600,
    borderRadius: '18px',
    boxShadow: '0 10px 22px rgba(227,39,45,0.18)',
  },
});

export function StopScreen({
  job,
  config,
  initial,
  profile,
  supervisorCandidates,
  siteContact,
  onNext,
  onShowInfo,
}: Props) {
  const styles = useStyles();
  const now = new Date();
  const [swmsReference, setSwmsReference] = useState(initial.swmsReference || '');
  const [swmsOptions, setSwmsOptions] = useState<Array<{ id: string; label: string; reference: string }>>([]);

  const defaultSupervisor = initial.supervisor
    || profile?.managerName
    || (supervisorCandidates[0]?.displayName ?? '');
  const [supervisor, setSupervisor] = useState<string>(defaultSupervisor);
  const [supervisorQuery, setSupervisorQuery] = useState<string>(defaultSupervisor);

  const supervisorOptions = useMemo(() => {
    const list: SupervisorCandidate[] = [...supervisorCandidates];
    const managerName = profile?.managerName?.trim();
    if (managerName && !list.some(c => c.displayName === managerName)) {
      list.unshift({
        displayName: managerName,
        email: profile?.managerEmail || '',
        positionTitle: 'Manager',
      });
    }
    return list;
  }, [supervisorCandidates, profile]);

  // Derive editable PPE list from admin-managed config + any extras the user
  // already had selected (preserves continuity if user navigated back).
  const initialPPE: PPEItemState[] = useMemo(() => {
    const defaultLabels = new Set(config.ppe.map(p => p.label));
    const fromConfig: PPEItemState[] = config.ppe.map(p => ({
      id: `ppe-${p.id}`,
      label: p.label,
      on: !!initial.ppeSelected?.includes(p.label),
      custom: false,
    }));
    const customs: PPEItemState[] = (initial.ppeSelected || [])
      .filter(label => !defaultLabels.has(label))
      .map((label, i) => ({
        id: `cppe-${i}-${Date.now()}`,
        label,
        on: true,
        custom: true,
      }));
    return [...fromConfig, ...customs];
  }, [config.ppe, initial.ppeSelected]);

  const [ppe, setPPE] = useState<PPEItemState[]>(initialPPE);
  const [customInput, setCustomInput] = useState('');

  useEffect(() => {
    let cancelled = false;
    getJobSwms(job.jobNumber)
      .then(records => {
        if (cancelled) return;
        const options = records.map(record => ({
          id: record.id,
          reference: record.swmsReference,
          label: `${record.swmsReference} · ${record.templateTitle}`,
        }));
        setSwmsOptions(options);
      })
      .catch(() => {
        if (!cancelled) setSwmsOptions([]);
      });
    return () => { cancelled = true; };
  }, [job.jobNumber]);

  const togglePPE = (id: string) => {
    setPPE(p => p.map(x => x.id === id ? { ...x, on: !x.on } : x));
  };
  const addCustomPPE = () => {
    const v = customInput.trim();
    if (!v) return;
    setPPE(p => [...p, { id: `cppe-${Date.now()}`, label: v, on: true, custom: true }]);
    setCustomInput('');
  };
  const removePPE = (id: string) => {
    setPPE(p => p.filter(x => x.id !== id));
  };

  const handleNext = () => {
    onNext({
      date: formatDateAU(now),
      time: formatTimeAU(now),
      taskDescription: (job.jobName || '').trim() || 'Untitled job',
      siteAddress: `${job.addressLine1}, ${job.suburb} ${job.postcode}`,
      supervisor,
      workOrderNumber: job.jobNumber,
      swmsReference,
      ppeSelected: ppe.filter(p => p.on).map(p => p.label),
      siteContactName: siteContact?.fullName,
      siteContactRole: siteContact?.role,
      siteContactPhone: siteContact?.bestPhone,
    });
  };

  return (
    <>
      <PhaseStrip step={1} />
      <div className={styles.body} data-screen-body>
        <div className={styles.banner}>
          <strong>STOP.</strong> Before starting, confirm the job details and tell us what PPE you're wearing. Take a full 5 minutes — this step saves lives.
        </div>

        <Card className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.stepNum}>1</span>
            Job Details
            <Button
              appearance="subtle"
              icon={<Info16Regular />}
              onClick={() => onShowInfo('stop')}
              className={styles.infoBtn}
              aria-label="More info"
            />
          </div>

          <div className={styles.fieldGrid2}>
            <ReadOnlyField label="Date" value={formatDateAU(now)} source="AUTO" />
            <ReadOnlyField label="Time" value={formatTimeAU(now)} source="AUTO" />
          </div>

          <ReadOnlyField label="Job Name" value={(job.jobName || '').trim() || 'Untitled job'} source="ASCORA" />

          <ReadOnlyField
            label="Site / address"
            value={`${job.addressLine1}, ${job.suburb} ${job.postcode}`}
            source="ASCORA"
          />

          <Field
            label={
              <span>
                Supervisor
                {profile?.managerName && supervisor === profile.managerName && (
                  <SourceTag source="M365" />
                )}
              </span>
            }
            style={{ marginBottom: '12px' }}
          >
            <Combobox
              value={supervisorQuery}
              selectedOptions={[supervisor]}
              size="large"
              onOptionSelect={(_, data) => {
                if (data.optionValue) {
                  setSupervisor(data.optionValue);
                  setSupervisorQuery(data.optionText || data.optionValue);
                }
              }}
              onChange={(e) => {
                const v = (e.target as HTMLInputElement).value;
                setSupervisorQuery(v);
                setSupervisor(v);
              }}
              placeholder="Select supervisor"
              freeform
              positioning="below"
              input={{
                style: { fontSize: '16px' }, // suppresses iOS focus zoom
                autoCapitalize: 'words',
                autoComplete: 'off',
              }}
              listbox={{ style: { maxHeight: '50vh' } }}
            >
              {supervisorOptions.map(opt => (
                <Option
                  key={opt.email || opt.displayName}
                  value={opt.displayName}
                  text={opt.displayName}
                >
                  {opt.displayName}{opt.positionTitle ? ` · ${opt.positionTitle}` : ''}
                </Option>
              ))}
            </Combobox>
          </Field>

          <ReadOnlyField label="Job / Work Order" value={job.jobNumber} source="ASCORA" />

          {siteContact && (siteContact.fullName || siteContact.bestPhone) && (
            <ReadOnlyField
              label="Site contact"
              source="ASCORA"
              value={
                <>
                  {siteContact.fullName || '—'}
                  {siteContact.role ? ` · ${siteContact.role}` : ''}
                  {siteContact.bestPhone ? (
                    <>
                      {' · '}
                      <a
                        href={`tel:${siteContact.bestPhone.replace(/[^\d+]/g, '')}`}
                        style={{ color: '#C21F24', fontWeight: 600 }}
                      >
                        {siteContact.bestPhone}
                      </a>
                    </>
                  ) : null}
                </>
              }
            />
          )}

          <Field label="Completed SWMS / SOP reference (if applicable)">
            {swmsOptions.length > 0 ? (
              <Combobox
                value={swmsReference}
                selectedOptions={swmsReference ? [swmsReference] : []}
                size="large"
                onOptionSelect={(_, data) => {
                  setSwmsReference(data.optionValue ?? '');
                }}
                onChange={(e) => setSwmsReference((e.target as HTMLInputElement).value)}
                placeholder="Select completed SWMS"
                freeform
                positioning="below"
                input={{ style: { fontSize: '16px' }, autoComplete: 'off' }}
              >
                <Option value="" text="">
                  None / not applicable
                </Option>
                {swmsOptions.map(opt => (
                  <Option key={opt.id} value={opt.reference} text={opt.reference}>
                    {opt.label}
                  </Option>
                ))}
              </Combobox>
            ) : (
              <Input
                value={swmsReference}
                size="large"
                onChange={(_, data) => setSwmsReference(data.value)}
                placeholder="Complete SWMS first or enter reference"
                input={{ style: { fontSize: '16px' }, autoComplete: 'off' }}
              />
            )}
          </Field>
        </Card>

        <Card className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.stepNum}>2</span>
            PPE I have on me
            <Button
              appearance="subtle"
              icon={<Info16Regular />}
              onClick={() => onShowInfo('ppe')}
              className={styles.infoBtn}
              aria-label="More info"
            />
          </div>
          <div style={{ fontSize: '13px', color: tokens.colorNeutralForeground3, marginBottom: 10 }}>
            Tap each PPE item you're wearing.
          </div>
          <div className={styles.ppeGrid}>
            {ppe.map(p => (
              <div key={p.id} className={styles.ppeChipWrap}>
                <TapRow
                  variant="chip"
                  checked={p.on}
                  onToggle={() => togglePPE(p.id)}
                >
                  {p.label}
                </TapRow>
                {p.custom && (
                  <button
                    className={styles.ppeRemove}
                    onClick={(e) => { e.stopPropagation(); removePPE(p.id); }}
                    aria-label={`Remove ${p.label}`}
                  >
                    <Dismiss12Regular />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className={styles.customAddRow}>
            <Input
              value={customInput}
              size="large"
              onChange={(_, data) => setCustomInput(data.value)}
              onKeyDown={e => e.key === 'Enter' && addCustomPPE()}
              placeholder="Add custom PPE..."
              style={{ flex: 1 }}
              input={{ style: { fontSize: '16px' }, autoComplete: 'off' }}
            />
            <Button
              appearance="primary"
              icon={<Add20Regular />}
              onClick={addCustomPPE}
              size="large"
            >
              Add
            </Button>
          </div>
        </Card>

        <Button
          appearance="primary"
          size="large"
          onClick={handleNext}
          className={styles.cta}
        >
          Next · Identify Hazards
        </Button>
      </div>
    </>
  );
}
